/**
 * Schedules (M5): reminders, monitors and the daily brief. One Durable Object alarm per person
 * runs what is due; these are the specs each row carries and the product rules around them.
 */
import { z } from 'zod';
import { type Lang, localDate, zonedMidnight } from './calendar-window';

export const ScheduleKind = z.enum(['reminder', 'monitor', 'brief']);
export type ScheduleKind = z.infer<typeof ScheduleKind>;

export const ScheduleStatus = z.enum(['active', 'paused', 'done', 'failed']);
export type ScheduleStatus = z.infer<typeof ScheduleStatus>;

export const ReminderSpec = z.object({
  kind: z.literal('reminder'),
  what: z.string().min(1).max(500),
  /** ISO instant. */
  at: z.string(),
  lang: z.enum(['en', 'ar']),
});
export type ReminderSpec = z.infer<typeof ReminderSpec>;

/** A mail monitor: new mail matched by the classifier against `what`. */
export const MailMonitorSpec = z.object({
  kind: z.literal('monitor'),
  watch: z.literal('mail'),
  what: z.string().min(1).max(500),
  connection_id: z.string(),
  provider: z.enum(['google', 'microsoft']),
  /** Provider cursor (Gmail historyId / Graph deltaLink); empty until the first poll. */
  cursor: z.string().optional(),
  keep_watching: z.boolean().default(false),
  started_at: z.string(),
  expires_at: z.string(),
  lang: z.enum(['en', 'ar']),
  matched: z.number().int().default(0),
  /** Message ids already judged (the last fifty), so a re-delivered change is not judged twice. */
  seen: z.array(z.string()).default([]),
});
export type MailMonitorSpec = z.infer<typeof MailMonitorSpec>;

/** Meeting follow-through: does the attendee reply within 48 hours; one report at 24 hours. */
export const ReplyMonitorSpec = z.object({
  kind: z.literal('monitor'),
  watch: z.literal('reply'),
  connection_id: z.string(),
  provider: z.enum(['google', 'microsoft']),
  event_id: z.string(),
  title: z.string(),
  attendees: z.array(z.object({ email: z.string(), name: z.string().optional() })),
  started_at: z.string(),
  /** When the one report goes out (24 h after the invite). */
  report_at: z.string(),
  reported: z.boolean().default(false),
  expires_at: z.string(),
  lang: z.enum(['en', 'ar']),
});
export type ReplyMonitorSpec = z.infer<typeof ReplyMonitorSpec>;

/**
 * M5.4: the reply watch on a mail TWO sent. Polls the sending mailbox for a message from one
 * of the recipients received after the send; one proactive line when it arrives.
 */
export const MailReplyMonitorSpec = z.object({
  kind: z.literal('monitor'),
  watch: z.literal('mail_reply'),
  connection_id: z.string(),
  provider: z.enum(['google', 'microsoft']),
  /** The sent message (its id and thread, when the provider gave them). */
  message_id: z.string(),
  thread_id: z.string().optional(),
  subject: z.string(),
  recipients: z.array(z.string()).min(1),
  sent_at: z.string(),
  started_at: z.string(),
  expires_at: z.string(),
  lang: z.enum(['en', 'ar']),
  /** Message ids already judged, so a message is never judged twice. */
  seen: z.array(z.string()).default([]),
  /** Set once a reply was detected. */
  replied: z
    .object({ from: z.string(), at: z.string(), text: z.string(), accepted: z.boolean() })
    .optional(),
});
export type MailReplyMonitorSpec = z.infer<typeof MailReplyMonitorSpec>;

export const MonitorSpec = z.discriminatedUnion('watch', [
  MailMonitorSpec,
  ReplyMonitorSpec,
  MailReplyMonitorSpec,
]);
export type MonitorSpec = z.infer<typeof MonitorSpec>;

export const BriefSpec = z.object({
  kind: z.literal('brief'),
  /** Local time "HH:MM". */
  time: z.string().regex(/^\d{2}:\d{2}$/),
  timezone: z.string(),
  lang: z.enum(['en', 'ar']),
});
export type BriefSpec = z.infer<typeof BriefSpec>;

export const ScheduleSpec = z.union([ReminderSpec, MonitorSpec, BriefSpec]);
export type ScheduleSpec = z.infer<typeof ScheduleSpec>;

/** Monitors poll every five minutes. */
export const MONITOR_POLL_MS = 5 * 60_000;
/** At most five active monitors per person. */
export const MONITOR_MAX_ACTIVE = 5;
/** A monitor lives seven days. */
export const MONITOR_TTL_MS = 7 * 24 * 3_600_000;
/** Follow-through: watch 48 hours, report once at 24 hours. */
export const FOLLOW_THROUGH_WINDOW_MS = 48 * 3_600_000;
export const FOLLOW_THROUGH_REPORT_MS = 24 * 3_600_000;
/** M5.4: a reply watch polls every minute for the first ten minutes, then every five, for 48 h. */
export const REPLY_POLL_FAST_MS = 60_000;
export const REPLY_FAST_WINDOW_MS = 10 * 60_000;
export const REPLY_POLL_SLOW_MS = 5 * 60_000;
export const REPLY_WATCH_WINDOW_MS = 48 * 3_600_000;

/** The delay to the next reply poll, from the send time. */
export function replyPollDelayMs(sentAt: string, now: Date): number {
  const since = now.getTime() - new Date(sentAt).getTime();
  return since < REPLY_FAST_WINDOW_MS ? REPLY_POLL_FAST_MS : REPLY_POLL_SLOW_MS;
}
/** Default brief time, local. */
export const BRIEF_DEFAULT_TIME = '07:30';

export function parseSpec(json: string): ScheduleSpec | undefined {
  try {
    const parsed = ScheduleSpec.safeParse(JSON.parse(json));
    return parsed.success ? parsed.data : undefined;
  } catch {
    return undefined;
  }
}

/** The next local occurrence of "HH:MM" strictly after `now`, in the zone. */
export function nextDailyRun(time: string, timezone: string, now: Date): Date {
  const [h, m] = time.split(':').map(Number) as [number, number];
  const l = localDate(now, timezone);
  const today = zonedMidnight(l.year, l.month, l.day, timezone).getTime() + (h * 60 + m) * 60_000;
  if (today > now.getTime()) return new Date(today);
  const t = new Date(Date.UTC(l.year, l.month - 1, l.day + 1, 12));
  return new Date(
    zonedMidnight(t.getUTCFullYear(), t.getUTCMonth() + 1, t.getUTCDate(), timezone).getTime() +
      (h * 60 + m) * 60_000,
  );
}

/** "Watching · next check in 4 min" / "يراقب · الفحص التالي بعد 4 دقائق". */
export function watchingLine(nextRunAt: string, now: Date, lang: Lang): string {
  const minutes = Math.max(0, Math.ceil((new Date(nextRunAt).getTime() - now.getTime()) / 60_000));
  if (lang === 'ar') {
    const unit =
      minutes === 1 ? 'دقيقة' : minutes === 2 ? 'دقيقتين' : minutes <= 10 ? 'دقائق' : 'دقيقة';
    const n = minutes === 1 ? '' : minutes === 2 ? '' : `${minutes} `;
    return `يراقب · الفحص التالي بعد ${n}${unit}`;
  }
  return `Watching · next check in ${minutes} min`;
}

/** The task title for a schedule as the Tasks screen shows it. */
export function scheduleTitle(spec: ScheduleSpec, lang: Lang): string {
  switch (spec.kind) {
    case 'reminder':
      return /^\s*(?:تذكير|reminder)(?=[\s:：]|$)/iu.test(spec.what)
        ? spec.what
        : lang === 'ar'
          ? `تذكير: ${spec.what}`
          : `Reminder: ${spec.what}`;
    case 'monitor':
      if (spec.watch === 'mail')
        return lang === 'ar' ? `يراقب: ${spec.what}` : `Watching: ${spec.what}`;
      if (spec.watch === 'mail_reply')
        return lang === 'ar'
          ? `بانتظار رد ${spec.recipients.join('، ')}`
          : `Waiting for ${spec.recipients.join(', ')} to reply`;
      return lang === 'ar'
        ? `بانتظار رد ${firstNames(spec.attendees)} على "${spec.title}"`
        : `Waiting for ${firstNames(spec.attendees)} to reply to "${spec.title}"`;
    case 'brief':
      return lang === 'ar' ? 'الملخص اليومي' : 'Daily brief';
  }
}

function firstNames(list: readonly { email: string; name?: string | undefined }[]): string {
  return list.map((a) => a.name?.split(' ')[0] || a.email.split('@')[0]).join(', ');
}

/** Follow-through lines: "Alex accepted." / "قَبِل أليكس." and "No reply from Alex yet." */
export function followThroughLine(
  attendee: { email: string; name?: string | undefined },
  response: string | undefined,
  lang: Lang,
): string {
  const name = attendee.name?.split(' ')[0] || attendee.email.split('@')[0] || attendee.email;
  const r = (response ?? '').toLowerCase();
  if (r === 'accepted') return lang === 'ar' ? `قَبِل ${name}.` : `${name} accepted.`;
  if (r === 'declined') return lang === 'ar' ? `اعتذر ${name}.` : `${name} declined.`;
  if (r === 'tentative' || r === 'tentativelyaccepted')
    return lang === 'ar' ? `${name} غير متأكد بعد.` : `${name} is tentative.`;
  return lang === 'ar' ? `ما رد ${name} بعد.` : `No reply from ${name} yet.`;
}

/** The "waiting for a reply" checklist line added when an invite goes out. */
export function waitingForReplyLine(
  attendees: readonly { email: string; name?: string | undefined }[],
  lang: Lang,
): string {
  const names = firstNames(attendees);
  return lang === 'ar' ? `بانتظار رد ${names}.` : `Waiting for a reply from ${names}.`;
}

/** The one proactive line for a matched mail monitor. */
export function monitorMatchLine(
  item: { from: { email: string; name?: string | undefined }; subject: string },
  lang: Lang,
): string {
  const who = item.from.name || item.from.email;
  return lang === 'ar'
    ? `وصل: ${who} كتب "${item.subject}".`
    : `Arrived: ${who} wrote "${item.subject}".`;
}

/** M5.4: the promise on the send-time reply and the checklist. */
export function waitingForMailReplyLine(address: string, lang: Lang): string {
  return lang === 'ar'
    ? `بانتظار رد ${address}. سأخبرك حين يصل.`
    : `Waiting for ${address}'s reply. I will tell you when it arrives.`;
}

/** The first non-empty line of a reply, trimmed to one sentence's worth. */
export function firstLineOf(text: string, max = 160): string {
  const line =
    text
      .split(/\r?\n/u)
      .map((l) => l.trim())
      .find((l) => l && !l.startsWith('>')) ?? '';
  return line.length > max ? `${line.slice(0, max - 1)}…` : line;
}

/** The one proactive line when a reply arrives: quoted, plus "confirmed" on acceptance. */
export function replyDetectedLine(
  address: string,
  firstLine: string,
  accepted: boolean,
  lang: Lang,
): string {
  const quoted =
    lang === 'ar' ? `ردّ ${address}: "${firstLine}".` : `${address} replied: "${firstLine}".`;
  if (!accepted) return quoted;
  return lang === 'ar' ? `${quoted} الاجتماع مؤكد.` : `${quoted} The meeting is confirmed.`;
}

export const REPLY_RECEIVED_LINE = { en: 'Reply received.', ar: 'وصل الرد.' } as const;

/** "Watching for a reply · next check in N min" / «أراقب الرد · الفحص التالي بعد N د». */
export function watchingForReplyLine(nextRunAt: string, now: Date, lang: Lang): string {
  const minutes = Math.max(0, Math.ceil((new Date(nextRunAt).getTime() - now.getTime()) / 60_000));
  return lang === 'ar'
    ? `أراقب الرد · الفحص التالي بعد ${minutes} د`
    : `Watching for a reply · next check in ${minutes} min`;
}

/** The end of a watch without a reply. */
export function noReplyLine(address: string, lang: Lang): string {
  return lang === 'ar'
    ? `لم يصل رد من ${address} خلال يومين.`
    : `No reply from ${address} in two days.`;
}
